import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { supabase } from '../../services/supabaseClient';
import { RESERVA_BASE } from './reserva';
import { useNoIndex } from './RivieraInventario';
import {
  STATUS, podeEditar, listarCasas, salvarCasa, apagarCasa, adicionarFoto, retirarFoto, fotoCapa,
  preencherComIA, formatarPreco,
} from './casasApi';

/*
  Área do Fábio para cadastrar as casas que ele vende na Riviera (tarefa 87c272e9).
  Pedido do João em 16/10: o cadastro fica no próprio site, não dentro da Bianca,
  e a IA da casa ajuda a preencher. Ele entra com o mesmo e-mail e senha da Bianca;
  quem pode editar é decidido no banco (site_pode_editar), não aqui. Feita para o
  celular: ele pega os dados na casa, com o proprietário.
*/

const azul = '#005BAA';
const tinta = '#13314e';

const Page = styled.main`
  min-height: 100svh; background: #f7fbfd; color: ${tinta};
  font-family: 'Montserrat', 'Helvetica Neue', Arial, sans-serif;
`;
const Top = styled.header`
  background: #fff; border-bottom: 1px solid #e3edf5; padding: 10px 16px;
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  img { height: 64px; width: auto; margin: -10px 0; }
  .sair { background: none; border: 1px solid #c9dbea; color: ${tinta}; border-radius: 999px; padding: 8px 14px; font-weight: 700; cursor: pointer; }
`;
const Body = styled.section`
  /* O CSS global do site centraliza o texto; formulário centralizado fica difícil de ler no celular. */
  max-width: 760px; margin: 0 auto; padding: 20px 16px 80px; text-align: left;
  h1 { font-size: 24px; margin: 0 0 4px; }
  .sub { color: #56708a; margin: 0 0 18px; }
`;
const Botao = styled.button`
  border: none; border-radius: 999px; padding: 13px 20px; font-weight: 800; font-size: 15px; cursor: pointer;
  background: ${(p) => (p.$tipo === 'perigo' ? '#fff' : p.$tipo === 'leve' ? '#eef5fb' : azul)};
  color: ${(p) => (p.$tipo === 'perigo' ? '#b42318' : p.$tipo === 'leve' ? tinta : '#fff')};
  border: ${(p) => (p.$tipo === 'perigo' ? '1px solid #f1b2ac' : 'none')};
  &:disabled { opacity: 0.55; cursor: wait; }
`;
const Caixa = styled.div`
  background: #fff; border: 1px solid #e3edf5; border-radius: 16px; padding: 18px; margin-bottom: 16px;
  box-shadow: 0 4px 16px rgba(19,49,78,0.06);
  h2 { font-size: 17px; margin: 0 0 10px; }
  .dica { color: #56708a; font-size: 14px; margin: 0 0 10px; }
`;
const Campos = styled.div`
  display: grid; gap: 12px; grid-template-columns: 1fr 1fr;
  .inteiro { grid-column: 1 / -1; }
  label { display: grid; gap: 5px; font-size: 13px; font-weight: 700; color: #3c5873; }
  input, select, textarea { font: inherit; font-size: 16px; padding: 11px 12px; border: 1px solid #c9dbea; border-radius: 10px; background: #fff; color: ${tinta}; width: 100%; box-sizing: border-box; }
  textarea { min-height: 110px; resize: vertical; }
  .check { display: flex; align-items: center; gap: 8px; font-size: 15px; font-weight: 600; }
  .check input { width: auto; }
`;
const Lista = styled.div`
  display: grid; gap: 12px;
  button.item { display: grid; grid-template-columns: 96px 1fr; gap: 12px; align-items: center; text-align: left;
    background: #fff; border: 1px solid #e3edf5; border-radius: 14px; padding: 10px; cursor: pointer; font: inherit; color: inherit; }
  img { width: 96px; height: 72px; object-fit: cover; border-radius: 10px; background: #e3edf5; }
  .t { font-weight: 800; }
  .m { color: #56708a; font-size: 13px; margin-top: 3px; }
`;
const Selo = styled.span`
  display: inline-block; font-size: 12px; font-weight: 700; padding: 3px 9px; border-radius: 999px; margin-left: 6px;
  background: ${(p) => ({ disponivel: '#e7f6ec', reservado: '#fff4e5', vendido: '#eceff3', indisponivel: '#eceff3' }[p.$s] || '#eceff3')};
  color: ${(p) => ({ disponivel: '#1a7f3c', reservado: '#8a4b00' }[p.$s] || '#4b5563')};
`;
const Fotos = styled.div`
  display: grid; gap: 10px; grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  figure { margin: 0; background: #f2f7fb; border-radius: 12px; overflow: hidden; position: relative; }
  img { width: 100%; aspect-ratio: 4 / 3; object-fit: cover; display: block; }
  figcaption { display: flex; gap: 6px; padding: 6px; flex-wrap: wrap; }
  figcaption button { font-size: 12px; padding: 5px 9px; border-radius: 999px; border: 1px solid #c9dbea; background: #fff; cursor: pointer; }
  .capa { position: absolute; top: 6px; left: 6px; background: ${azul}; color: #fff; font-size: 11px; font-weight: 800; padding: 3px 8px; border-radius: 999px; }
  .nova { position: absolute; top: 6px; left: 6px; background: #fff4e5; color: #8a4b00; font-size: 11px; font-weight: 800; padding: 3px 8px; border-radius: 999px; }
`;
const Aviso = styled.p`
  margin: 10px 0 0; font-size: 14px; color: ${(p) => (p.$erro ? '#b42318' : '#1a7f3c')};
`;

const VAZIO = {
  id: null, titulo: '', preco: '', area_construida: '', area_terreno: '', quartos: '', suites: '', vagas: '',
  condominio_mensal: '', iptu_anual: '', aceita_fgts: '', mobiliado: '', descricao: '', status: 'disponivel', destaque: false,
};

// Banco devolve número; o formulário trabalha com texto, para o campo poder ficar vazio.
function paraFormulario(c) {
  const f = { ...VAZIO };
  Object.keys(VAZIO).forEach((k) => {
    if (c[k] === null || c[k] === undefined) return;
    f[k] = typeof VAZIO[k] === 'boolean' ? !!c[k] : String(c[k]);
  });
  return f;
}

// "850 mil", "R$ 850.000,00" e "850000" viram 850000 antes de ir ao banco.
function numero(v) {
  const t = String(v ?? '').trim().toLowerCase();
  if (!t) return '';
  // "980 mil" e "1,2 milhão": o Fábio escreve preço como fala (teste de estresse de 17/10).
  const milhao = /milh[aã]o|milh[oõ]es|\bmi\b/.test(t);
  const mil = !milhao && /mil\b/.test(t);
  // Tira a unidade antes dos dígitos: o "2" de "m2" grudaria no número (600m2 viraria 6002).
  const limpo = t.replace(/m[²2]/g, '').replace(/[^\d,.]/g, '').replace(/\.(?=\d{3}(\D|$))/g, '').replace(',', '.');
  const n = Number(limpo);
  if (!Number.isFinite(n)) return '';
  return String(milhao ? Math.round(n * 1000000) : mil ? Math.round(n * 1000) : n);
}

function inteiro(v) {
  const n = numero(v);
  return n === '' ? '' : String(Math.round(Number(n)));
}

function Login({ aoEntrar }) {
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState('');
  const [indo, setIndo] = useState(false);
  const entrar = async (e) => {
    e.preventDefault();
    setIndo(true); setErro('');
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password: senha });
    setIndo(false);
    if (error) setErro('E-mail ou senha não conferem. Use o mesmo acesso da Bianca.');
    else aoEntrar();
  };
  return (
    <Caixa as="form" onSubmit={entrar}>
      <h2>Entrar</h2>
      <p className="dica">Use o mesmo e-mail e senha que você usa na Bianca.</p>
      <Campos>
        <label className="inteiro">E-mail<input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required /></label>
        <label className="inteiro">Senha<input type="password" autoComplete="current-password" value={senha} onChange={(e) => setSenha(e.target.value)} required /></label>
      </Campos>
      <div style={{ marginTop: 14 }}><Botao type="submit" disabled={indo}>{indo ? 'Entrando…' : 'Entrar'}</Botao></div>
      {erro && <Aviso $erro>{erro}</Aviso>}
    </Caixa>
  );
}

function Formulario({ casa, aoTerminar }) {
  const [f, setF] = useState(() => (casa ? paraFormulario(casa) : { ...VAZIO }));
  const [texto, setTexto] = useState('');
  const [ia, setIa] = useState({ indo: false, msg: '', erro: false });
  const [novas, setNovas] = useState([]);
  const [fotos, setFotos] = useState(casa?.fotos || []);
  const [estado, setEstado] = useState({ indo: false, msg: '', erro: false });
  const muda = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  useEffect(() => () => novas.forEach((n) => URL.revokeObjectURL(n.previa)), [novas]);

  const preencher = async () => {
    setIa({ indo: true, msg: '', erro: false });
    try {
      const c = await preencherComIA(texto);
      setF((x) => {
        const y = { ...x };
        // A IA só preenche o que achou no texto; o que veio vazio não apaga o que já estava escrito.
        ['titulo', 'preco', 'area_construida', 'area_terreno', 'quartos', 'suites', 'vagas', 'condominio_mensal', 'iptu_anual', 'mobiliado', 'descricao']
          .forEach((k) => { if (c[k] !== null && c[k] !== undefined && c[k] !== '') y[k] = String(c[k]); });
        if (c.aceita_fgts !== null && c.aceita_fgts !== undefined) y.aceita_fgts = String(c.aceita_fgts);
        return y;
      });
      const falta = (c.faltando || []).join(', ');
      setIa({ indo: false, msg: falta ? `Pronto. Confira tudo. Faltou no texto: ${falta}.` : 'Pronto. Confira tudo antes de salvar.', erro: false });
    } catch (e) {
      setIa({ indo: false, msg: e.message, erro: true });
    }
  };

  const escolherFotos = (e) => {
    const arqs = Array.from(e.target.files || []).filter((a) => a.type.startsWith('image/'));
    setNovas((x) => [...x, ...arqs.map((a) => ({ arquivo: a, previa: URL.createObjectURL(a), chave: `${a.name}-${a.size}-${Math.random()}` }))]);
    e.target.value = '';
  };

  const salvar = async () => {
    if (!f.titulo.trim()) { setEstado({ indo: false, msg: 'Dê um título para a casa.', erro: true }); return; }
    setEstado({ indo: true, msg: 'Salvando…', erro: false });
    try {
      const payload = {
        ...f,
        preco: numero(f.preco), area_construida: numero(f.area_construida), area_terreno: numero(f.area_terreno),
        condominio_mensal: numero(f.condominio_mensal), iptu_anual: numero(f.iptu_anual),
        // "3 quartos" no campo não pode quebrar o salvar: vira 3.
        quartos: inteiro(f.quartos), suites: inteiro(f.suites), vagas: inteiro(f.vagas),
      };
      const id = await salvarCasa(payload);
      const falhas = [];
      for (let i = 0; i < novas.length; i += 1) {
        setEstado({ indo: true, msg: `Subindo foto ${i + 1} de ${novas.length}…`, erro: false });
        // Uma foto que falha não perde a casa nem as outras fotos, mas tem de aparecer no fim:
        // em 16/10 a tela disse "salva com as fotos" com as duas recusadas pelo banco.
        try { await adicionarFoto(id, novas[i].arquivo); } catch (e) { falhas.push(e.message || 'erro'); }
      }
      if (falhas.length) {
        aoTerminar(`Casa salva, mas ${falhas.length} de ${novas.length} foto(s) não subiram (${falhas[0]}). Abra a casa e adicione de novo.`, true);
      } else {
        aoTerminar(novas.length ? `Casa salva com ${novas.length} foto(s).` : 'Casa salva.');
      }
    } catch (e) {
      setEstado({ indo: false, msg: `Não deu para salvar: ${e.message}`, erro: true });
    }
  };

  const tirarFoto = async (foto) => {
    if (!window.confirm('Tirar esta foto da casa?')) return;
    try { await retirarFoto(foto.id); setFotos((x) => x.filter((y) => y.id !== foto.id)); } catch (e) { window.alert(e.message); }
  };
  const virarCapa = async (foto) => {
    try { await fotoCapa(foto.id); setFotos((x) => [{ ...foto, capa: true }, ...x.filter((y) => y.id !== foto.id).map((y) => ({ ...y, capa: false }))]); } catch (e) { window.alert(e.message); }
  };
  const apagar = async () => {
    if (!window.confirm('Apagar esta casa do site? Ela some da página de casas na hora.')) return;
    setEstado({ indo: true, msg: 'Apagando…', erro: false });
    try { await apagarCasa(casa.id); aoTerminar('Casa apagada.'); } catch (e) { setEstado({ indo: false, msg: e.message, erro: true }); }
  };

  return (
    <>
      <h1>{casa ? 'Editar casa' : 'Nova casa'}</h1>
      <p className="sub"><button type="button" onClick={() => aoTerminar('')} style={{ background: 'none', border: 'none', color: azul, fontWeight: 700, padding: 0, cursor: 'pointer' }}>← Voltar para a lista</button></p>
      <Caixa>
        <h2>Cole o que você tem da casa</h2>
        <p className="dica">A descrição do proprietário, um anúncio ou a conversa. A IA preenche os campos abaixo e você confere.</p>
        <Campos><label className="inteiro"><textarea value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Ex.: Casa na quadra F, 3 quartos sendo 2 suítes, piscina, terreno de 600 m², pedem 980 mil..." /></label></Campos>
        <div style={{ marginTop: 10 }}><Botao type="button" $tipo="leve" onClick={preencher} disabled={ia.indo || texto.trim().length < 20}>{ia.indo ? 'Lendo o texto…' : 'Preencher com IA'}</Botao></div>
        {ia.msg && <Aviso $erro={ia.erro}>{ia.msg}</Aviso>}
      </Caixa>
      <Caixa>
        <h2>Dados da casa</h2>
        <Campos>
          <label className="inteiro">Título<input value={f.titulo} onChange={muda('titulo')} maxLength={80} placeholder="Ex.: Casa 3 suítes com piscina, frente para o lago" /></label>
          <label>Preço (R$)<input inputMode="decimal" value={f.preco} onChange={muda('preco')} placeholder="980000" /></label>
          <label>Situação<select value={f.status} onChange={muda('status')}>{Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
          <label>Área construída (m²)<input inputMode="decimal" value={f.area_construida} onChange={muda('area_construida')} /></label>
          <label>Área do terreno (m²)<input inputMode="decimal" value={f.area_terreno} onChange={muda('area_terreno')} /></label>
          <label>Quartos<input inputMode="numeric" value={f.quartos} onChange={muda('quartos')} /></label>
          <label>Suítes<input inputMode="numeric" value={f.suites} onChange={muda('suites')} /></label>
          <label>Vagas<input inputMode="numeric" value={f.vagas} onChange={muda('vagas')} /></label>
          <label>Condomínio por mês (R$)<input inputMode="decimal" value={f.condominio_mensal} onChange={muda('condominio_mensal')} /></label>
          <label>IPTU por ano (R$)<input inputMode="decimal" value={f.iptu_anual} onChange={muda('iptu_anual')} /></label>
          <label>Aceita FGTS<select value={f.aceita_fgts} onChange={muda('aceita_fgts')}><option value="">Não informado</option><option value="true">Sim</option><option value="false">Não</option></select></label>
          <label className="inteiro">Mobília<select value={f.mobiliado} onChange={muda('mobiliado')}><option value="">Não informado</option><option value="nao">Sem móveis</option><option value="planejados">Com planejados</option><option value="completo">Mobiliada</option></select></label>
          <label className="inteiro">Descrição para o site<textarea value={f.descricao} onChange={muda('descricao')} maxLength={2000} /></label>
          <label className="inteiro check"><input type="checkbox" checked={f.destaque} onChange={muda('destaque')} /> Mostrar primeiro na página de casas</label>
        </Campos>
      </Caixa>
      <Caixa>
        <h2>Fotos</h2>
        <p className="dica">A primeira foto é a capa. Dá para escolher várias de uma vez; elas sobem quando você salvar.</p>
        <Fotos>
          {fotos.map((foto) => (
            <figure key={foto.id}>
              {foto.capa && <span className="capa">Capa</span>}
              <img src={foto.url} alt="" loading="lazy" />
              <figcaption>
                {!foto.capa && <button type="button" onClick={() => virarCapa(foto)}>Tornar capa</button>}
                <button type="button" onClick={() => tirarFoto(foto)}>Tirar</button>
              </figcaption>
            </figure>
          ))}
          {novas.map((n) => (
            <figure key={n.chave}>
              <span className="nova">Nova</span>
              <img src={n.previa} alt="" />
              <figcaption><button type="button" onClick={() => setNovas((x) => x.filter((y) => y.chave !== n.chave))}>Tirar</button></figcaption>
            </figure>
          ))}
        </Fotos>
        <label style={{ display: 'inline-block', marginTop: 12 }}>
          <input type="file" accept="image/*" multiple onChange={escolherFotos} style={{ display: 'none' }} />
          <span style={{ display: 'inline-block', background: '#eef5fb', color: tinta, padding: '12px 18px', borderRadius: 999, fontWeight: 800, cursor: 'pointer' }}>+ Adicionar fotos</span>
        </label>
      </Caixa>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <Botao type="button" onClick={salvar} disabled={estado.indo}>{estado.indo ? 'Salvando…' : 'Salvar casa'}</Botao>
        {casa && <Botao type="button" $tipo="perigo" onClick={apagar} disabled={estado.indo}>Apagar casa</Botao>}
      </div>
      {estado.msg && <Aviso $erro={estado.erro}>{estado.msg}</Aviso>}
    </>
  );
}

export default function RivieraAdmin() {
  useNoIndex();
  useEffect(() => { document.title = 'Cadastro de casas · Riviera de Santa Cristina'; }, []);
  const [sessao, setSessao] = useState(undefined);
  const [permitido, setPermitido] = useState(undefined);
  const [casas, setCasas] = useState(null);
  const [editando, setEditando] = useState(null);
  const [aviso, setAviso] = useState('');

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSessao(data.session || null));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSessao(s || null));
    return () => sub.subscription.unsubscribe();
  }, []);

  const carregar = useCallback(async () => {
    try { setCasas(await listarCasas()); } catch (e) { setAviso(`Não deu para carregar as casas: ${e.message}`); setCasas([]); }
  }, []);

  useEffect(() => {
    if (!sessao) { setPermitido(undefined); return; }
    podeEditar().then((ok) => { setPermitido(ok); if (ok) carregar(); }).catch(() => setPermitido(false));
  }, [sessao, carregar]);

  const [avisoErro, setAvisoErro] = useState(false);
  // Lista volta VAZIA ("Carregando…") até chegar a nova: com a velha na tela, o teste de estresse de 17/10 viu
  // "Nenhuma casa ainda" logo depois de salvar e a casa apagada ainda tocável (abrir dava erro).
  const terminar = async (msg, erro = false) => {
    setEditando(null); setCasas(null); setAviso(msg); setAvisoErro(erro); window.scrollTo(0, 0);
    await carregar();
  };
  const sair = async () => { await supabase.auth.signOut(); setCasas(null); setEditando(null); };
  const contagem = useMemo(() => (casas || []).filter((c) => c.status === 'disponivel').length, [casas]);

  return (
    <Page>
      <Top>
        <Link to={RESERVA_BASE}><img src="/riviera/logo.png" alt="Riviera de Santa Cristina XIII" /></Link>
        {sessao && <button type="button" className="sair" onClick={sair}>Sair</button>}
      </Top>
      <Body>
        {sessao === undefined && <p>Carregando…</p>}
        {sessao === null && (<><h1>Cadastro de casas</h1><p className="sub">Área do corretor para pôr as casas no site.</p><Login aoEntrar={() => {}} /></>)}
        {sessao && permitido === undefined && <p>Conferindo seu acesso…</p>}
        {sessao && permitido === false && (
          <Caixa><h2>Sem acesso a este cadastro</h2><p className="dica">Sua conta entrou, mas não está liberada para cadastrar casas neste site. Fale com o João.</p></Caixa>
        )}
        {sessao && permitido && editando !== null && (
          <Formulario key={editando === 'nova' ? 'nova' : editando.id} casa={editando === 'nova' ? null : editando} aoTerminar={terminar} />
        )}
        {sessao && permitido && editando === null && (
          <>
            <h1>Suas casas</h1>
            <p className="sub">{casas ? `${casas.length} cadastrada${casas.length === 1 ? '' : 's'}, ${contagem} no site agora.` : 'Carregando…'} <Link to={`${RESERVA_BASE}/casas`} style={{ color: azul, fontWeight: 700 }}>Ver a página de casas</Link></p>
            {aviso && <Aviso $erro={avisoErro}>{aviso}</Aviso>}
            <div style={{ margin: '14px 0 18px' }}><Botao type="button" onClick={() => { setAviso(''); setEditando('nova'); }}>+ Nova casa</Botao></div>
            <Lista>
              {(casas || []).map((c) => (
                <button key={c.id} type="button" className="item" onClick={() => { setAviso(''); setEditando(c); }}>
                  <img src={c.fotos?.[0]?.url || '/riviera/hero-peninsula.jpg'} alt="" />
                  <div>
                    <div className="t">{c.titulo}<Selo $s={c.status}>{STATUS[c.status] || c.status}</Selo></div>
                    <div className="m">{formatarPreco(c.preco) || 'Sem preço'} · {(c.fotos || []).length} foto{(c.fotos || []).length === 1 ? '' : 's'}</div>
                  </div>
                </button>
              ))}
            </Lista>
            {casas && casas.length === 0 && <p className="sub">Nenhuma casa ainda. Toque em "Nova casa" para começar.</p>}
          </>
        )}
      </Body>
    </Page>
  );
}
